/**
 * ViewportDataService - Type-safe reactive data slot registry.
 *
 * Decouples data producers (WorkflowService) from consumers (viewports).
 * Each slot has a compile-time type enforced via DataSlotMap.
 *
 * @example
 * ```typescript
 * const dataService = services.getService<ViewportDataService>(ViewportDataService.SERVICE_NAME);
 *
 * // Type-safe set/get
 * dataService.setSlot('ct-volume', volume);        // enforces Volume type
 * const v = dataService.getSlot('ct-volume');       // Volume | undefined
 *
 * // Type-safe subscription
 * dataService.subscribeToSlot('centerlines', (cls) => {
 *     cls.forEach(cl => console.log(cl.totalLength));
 * });
 * ```
 */

import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';
import type { Centerline } from '../domain/centerline';
import type { Volume } from '../domain/volume';

// ============================================================================
// Type-Safe Slot Definition
// ============================================================================

/**
 * Central map defining all data slots and their types.
 * To add a new slot, add one entry here — type safety is automatic.
 */
export interface DataSlotMap {
    /** Original CT volume for 2D slicing */
    'ct-volume': Volume;
    /** Processed volume for 3D rendering (may be masked/downsampled) */
    'rendering-volume': Volume;
    /** All centerlines from vascular description */
    centerlines: Map<string, Centerline>;
}

/** Valid slot names derived from DataSlotMap keys. */
export type DataSlotName = keyof DataSlotMap;

/** Helper type to get the data type for a given slot name. */
export type DataSlotValue<K extends DataSlotName> = DataSlotMap[K];

// ============================================================================
// Event Types
// ============================================================================

export interface SlotChangedPayload<K extends DataSlotName = DataSlotName> {
    slotName: K;
    value: DataSlotValue<K>;
}

export interface SlotClearedPayload {
    slotName: DataSlotName;
}

// ============================================================================
// ViewportDataService
// ============================================================================

export class ViewportDataService extends PubSubService {
    static readonly SERVICE_NAME = 'ViewportDataService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: ViewportDataService.SERVICE_NAME,
        factory: (context: ServiceContext) => new ViewportDataService(context),
    };

    readonly EVENTS = {
        SLOT_CHANGED: 'viewportdata:slot:changed',
        SLOT_CLEARED: 'viewportdata:slot:cleared',
        ALL_CLEARED: 'viewportdata:all:cleared',
    } as const;

    private readonly slots = new Map<DataSlotName, unknown>();

    constructor(_context: ServiceContext) {
        super();
    }

    /**
     * Set a slot value with compile-time type safety.
     */
    setSlot<K extends DataSlotName>(name: K, data: DataSlotValue<K>): void {
        this.slots.set(name, data);
        this.broadcast(this.EVENTS.SLOT_CHANGED, {
            slotName: name,
            value: data,
        } satisfies SlotChangedPayload<K>);
    }

    /**
     * Get a slot value with compile-time type safety.
     */
    getSlot<K extends DataSlotName>(name: K): DataSlotValue<K> | undefined {
        return this.slots.get(name) as DataSlotValue<K> | undefined;
    }

    /**
     * Check if a slot has a value.
     */
    hasSlot(name: DataSlotName): boolean {
        return this.slots.has(name);
    }

    /**
     * Clear a specific slot.
     */
    clearSlot(name: DataSlotName): void {
        if (this.slots.has(name)) {
            this.slots.delete(name);
            this.broadcast(this.EVENTS.SLOT_CLEARED, {
                slotName: name,
            } satisfies SlotClearedPayload);
        }
    }

    /**
     * Subscribe to changes for a specific slot with compile-time type safety.
     * The callback receives the typed slot value directly.
     */
    subscribeToSlot<K extends DataSlotName>(
        name: K,
        callback: (value: DataSlotValue<K>) => void
    ): () => void {
        return this.subscribe(this.EVENTS.SLOT_CHANGED, (payload: unknown) => {
            const typed = payload as SlotChangedPayload;
            if (typed.slotName === name) {
                callback(typed.value as DataSlotValue<K>);
            }
        });
    }

    /**
     * Clear all slots. Called when a case is closed.
     */
    clearAll(): void {
        this.slots.clear();
        this.broadcast(this.EVENTS.ALL_CLEARED);
    }

    override reset(): void {
        this.clearAll();
        super.reset();
    }
}
